import { Editor, Transforms, Node, Path } from 'slate';
import { nanoid } from 'nanoid';
import elementTypes from '../../elements/types';

import HelptapEditor from '../';

import * as paragraphUtilities from './paragraph';

const columnsCount = 2;

function buildColumnBlockOptions(index, width) {
  const column = {
    width,
    id: 'column-' + Date.now() + '-' + index,
    type: elementTypes.COLUMN
  };

  return column;
}

export function buildColumBlock(index, width) {

  const column = {
    ...buildColumnBlockOptions(index, width),
    children: [{
      ...buildColumnsBlockOptions(),
      children: [{
        ...buildColumnBlockOptions(0, 100),
        children: [HelptapEditor.paragraph.buildNode(),
          HelptapEditor.paragraph.buildNode()
        ]
      }]
    }]
  };

  return column;

}

export function select(editor, path) {

  const firstColumnFirstNode = [...path, 0, 0, 0, 0];

  Transforms.select(editor, firstColumnFirstNode);
  Transforms.collapse(editor, { edge: 'end' });

}

export function insert(editor, path) {

  const options = buildNode(columnsCount);

  Transforms.insertNodes(editor, options, { at: path });
  select(editor, path);
}

export function toggle(editor, currentNodePath) {

  const blockOptions = buildNode(columnsCount);

  Editor.withoutNormalizing(editor, () => {
    Transforms.insertNodes(editor, blockOptions, { at: currentNodePath });
    Transforms.delete(editor, { at: Path.next(currentNodePath) });
    select(editor, currentNodePath);
  });

}

export function handleDeleteBackward(editor, node, nodePath) {

  return false;

}

function buildColumnsBlockOptions() {

  const block = {
    type: elementTypes.COLUMNS,
    padding: [0, 0, 0, 0],
    margin: [0, 0, 0, 0],
    resizable: false,
    properties: {
      id: nanoid()
    }
  };

  return block;

}

export function buildNode(childrenCount) {

  const children = [];
  const width = 100 / childrenCount;

  for (let index = 0; index < childrenCount; index++) {

    const column = buildColumBlock(index, width);

    children.push(column);
  }

  const block = {
    ...buildColumnsBlockOptions(),
    children
  };

  return block;

}

export function cloneNode(node) {

  const clonedNode = HelptapEditor.cloneNode(node);
  const columns = clonedNode.columns;
  const columnsLength = columns.length;
  const duplicatedColumns = [];

  for (let index = 0; index < columnsLength; index++) {

    const column = columns[index];

    const duplicatedColumn = {
      ...column,
      id: 'column-' + Date.now() + '-' + index
    };

    const nodes = duplicatedColumn.content;
    const clonedNodes = [];

    for (let node of nodes) {

      let clonedNode = { ...node };

      if (node.type === elementTypes.HT_FILE) {
        clonedNode = HelptapEditor.file.cloneNode(node);
      } else if (node.type === elementTypes.FORM) {
        clonedNode = HelptapEditor.form.cloneNode(node);
      }

      clonedNodes.push(clonedNode);

    }

    duplicatedColumn.content = clonedNodes;

    duplicatedColumns.push(duplicatedColumn);

  }

  clonedNode.columns = duplicatedColumns;

  return clonedNode;

}

export function duplicate(editor, node, nodePath) {

  const clonedNode = cloneNode(node);

  HelptapEditor.insertNode(editor, clonedNode, nodePath);

}

export function updateColumnWidth(editor, columnPath, targetNewWidth, nextSiblingWidthDelta) {

  let nextColumnPath = Path.next(columnPath);
  let nextColumn;

  try {
    nextColumn = Node.get(editor, nextColumnPath);
  } catch (error) {
    console.error('Cannot find a descendant at path', nextColumnPath, 'in node');
    return;
  }

  Transforms.setNodes(editor, { width: targetNewWidth }, { at: columnPath });
  if (nextColumn) {
    Transforms.setNodes(editor, { width: +nextColumn.width + nextSiblingWidthDelta }, { at: nextColumnPath });
  }
}

function isLastNodeInColumn(editor, path) {

  const parentPath = Path.parent(path);
  const parent = Node.get(editor, parentPath);
  const index = path[path.length - 1];
  const nodesLength = parent.children.length;

  return index === nodesLength - 1;

}

export function tryMoveToNextTopLevelBlock(editor, node, path) {

  if (node.type !== elementTypes.DEFAULT) {
    return false;
  }

  if (HelptapEditor.isNodeEmptyByPath(editor, path) === false) {
    return false;
  }

  if (isLastNodeInColumn(editor, path) === false) {
    return false;
  }

  const paragraph = paragraphUtilities.buildNode();

  const insertPath = Path.next([path[0]]);

  HelptapEditor.insertBlock(editor, paragraph.type, paragraph, insertPath);
  HelptapEditor.focusNodeByPath(editor, insertPath);

  return true;

}

export function isColumnParent(editor) {

  const selection = editor.selection;

  if (!selection) {
    return false;
  }

  // Find the path to the selected node
  const [match] = Editor.nodes(editor, {
    at: selection,
    // This function is used to filter nodes. Return true if the node matches the condition.
    match: node => Node.isNode(node) && node.type === elementTypes.COLUMN,
  });

  // If a matching node is found, it means there's a 'column' parent node.
  return Boolean(match);
}

export function updateColumnsCount(editor, elementPath, columnsCount) {

  const [node] = Editor.node(editor, elementPath);

  const columns = node.children;
  const columnsLength = columns.length;
  const columnsCountDifference = columnsCount - columnsLength;

  if (columnsCountDifference === 0) {
    return;
  }

  let basePath = [...elementPath];

  if (columnsCountDifference > 0) {

    for (let index = 0; index < columnsCountDifference; index++) {
      const column = buildColumBlock(index, 50);
      Transforms.insertNodes(editor, column, { at: [...basePath, columnsLength + index] });
    }

  } else {

    for (let index = columnsCount; index < columnsLength; index++) {
      Transforms.removeNodes(editor, { at: [...basePath, columnsCount] });
    }

  }

  const totalColumns = columnsCount;
  const width = 100 / totalColumns;

  for (let index = 0; index < totalColumns; index++) {

    Transforms.setNodes(editor, { width }, { at: [...basePath, index] });

  }

}

export function updateColumnSpacing(editor, elementPath, columnSpacing) {

  const [node] = Editor.node(editor, elementPath);

  Transforms.setNodes(editor, { properties: { ...(node.properties || {}), columnSpacing } }, { at: elementPath });

}

export function isColumnsNode(node) {

  return node.type === elementTypes.COLUMNS;

}

export function updateColumnsOrder(editor, nodePath, positions) {

  Editor.withoutNormalizing(editor, () => {

    for (let position of positions) {

      const columnPath = [...nodePath, positions.indexOf(position)];
      const column = Node.get(editor, columnPath);

      Transforms.setNodes(editor, {
        properties: {
          ...column.properties,
          position
        }
      }, { at: columnPath });

    }

  });

}

export function updateNavigationSettings(editor, nodePath, navigationSettings) {

  Editor.withoutNormalizing(editor, () => {

    const node = Node.get(editor, nodePath);
    Transforms.setNodes(editor, {
      properties: {
        ...(node.properties || {}),
        navigationSettings
      }
    }, { at: nodePath });

  });

}

export function updateMiscellaneousSettings(editor, nodePath, settings) {

  Editor.withoutNormalizing(editor, () => {

    const node = Node.get(editor, nodePath);
    Transforms.setNodes(editor, {
      properties: {
        ...(node.properties || {}),
        ...settings
      }
    }, { at: nodePath });

  });

}